import { ReactElement, useEffect, useState } from "react";
import { IContentAreaProps, IFilterData, ILadleHistory, IState } from "../../../types";
import FilterComponentBase from "../../../components/filterComponentBase";
import { sortText } from "../../../system/utils";
import { useSelector } from "react-redux";
import { Alert } from "@mui/material";
import { ILadleData } from "./types";
import LadleTracking from "./components/ladleTracking";
import { IBarChartConfig, IPoint, ISeriesWithAxes } from "../../../components/charts/types";
import BarChart from "../../../components/charts/barChart";
import { useFetch } from "../../../system/useFetch";
import SkeletonView from "../../../components/skeletonView";


const Tracking = ({ parentSize }: IContentAreaProps): ReactElement => {
  const filter = useSelector<IState, IFilterData>(state => state.filter);
  const { loading, error, data } = useFetch<ILadleHistory[]>({ module: "meltShop", query: "getLadleHistory", payload: { ...filter.remote } });
  const [ ladleData, setLadleData ] = useState<ILadleData[] | null>(null);
  const [ barChartConfig, setBarChartConfig ] = useState<IBarChartConfig | null>(null);

  useEffect(() => {
    if (data) {
      const processedData: ILadleData[] = [];

      data.forEach(row => {
        let record = processedData.find(x => x.heatNumber === row.heatNumber);
        if(!record) {
          record = {
            ladle: row.ladle,
            color: row.color,
            heatNumber: row.heatNumber,
            steelCode: row.steelCode,
            tracking: []
          };

          processedData.push(record);
        }

        const trackingItem = {
          ladleHistoryID: row.ladleHistoryID,
          machineName: row.machineName,
          factoryDay: row.factoryDay,
          beginDatetime: row.beginDatetime,
          endDatetime: row.endDatetime,
          weight: row.weight,
          temperature: row.temperature,
          status: row.status
        };

        record.tracking.push(trackingItem);

      });

      if (processedData.length > 0) {
        const consolidate: any = {};
        let maximum = 0;

        processedData.map(row => {
          if (!consolidate[row.ladle]) {
            consolidate[row.ladle] = { count: 0, color: row.color };
          }

          consolidate[row.ladle].count += 1;

          maximum = Math.max(maximum, consolidate[row.ladle].count);

        });

        const categories = Object.keys(consolidate).sort(sortText);

        const barConfig: IBarChartConfig = {
          width: 600,
          xAxis: {
            name: "Ladles",
            categories
          },
          yAxis: [{
            minimum: 0,
            maximum,
            name: "Heats",
            ticksCount: 3,
            formatTick: (value) => {
              return typeof value === "number" ? (value as number).toFixed(2) : (value as IPoint).y.toFixed(2);
            }
          }],
          title: "Ladles by Heats",
          series: categories.map((key) => (
            {
              name: key,
              xAxisName: "Ladles",
              yAxisName: "Heats",
              value: consolidate[key].count,
              color: consolidate[key].color
            } as ISeriesWithAxes) ),
          showLegend: true
        };

        setBarChartConfig(barConfig);
      }

      setLadleData(processedData.length > 0 ? processedData : null);
    }
  }, [data]);


  const style = {
    ...parentSize,
    display: "grid",
    gridTemplateRows: "auto auto auto 1fr",
    gridTemplateColumns: "1fr",
    overflow: "hidden"
  };

  return(
    <div style={style} >
      <FilterComponentBase loading={loading}/>

      {loading && <SkeletonView  parentSize={parentSize}/>}
      {error && <Alert severity="warning">{error.message}</Alert>}
      {!loading && !error && !ladleData && <Alert severity="warning">No Data to Display.</Alert>}

      {!loading && !error && ladleData &&
        <>
          <div className="display-flex-row flex-wrap mb-1" style={{ height: 200 }}>
            <div style={{ maxWidth: 600 }}>
              {barChartConfig && <BarChart barChartConfig={barChartConfig}/>}
            </div>
          </div>
          <div className="header flex-grow-0" >
            <strong className="p-1 MuiTableCell-head" style={{ width: 120 }}>
              Ladle
            </strong>
            <strong className="p-1 MuiTableCell-head" style={{ width: 100 }}/>
            <strong className="p-1 text-center " style={{ width: 600 }}>
              Machines
            </strong>
          </div>
          <div style={{ overflowY: "scroll" }}>
            { ladleData.map((item, index) => {
              return <LadleTracking key={`ladle-tracking-${index}`} ladleData={item} />;
            })
            }
          </div>
        </>
      }
    </div>
  );
};

export default Tracking;
